import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "../../utils/swal";
import {
  Search,
  Plus,
  MessageSquare,
  Send,
  ChevronDown,
  Check,
  Filter,
} from "lucide-react";
import { Drawer } from "../../components/ui";
import CreateTicketModal from "./CreateTicketModal";
import SupportAvatar from "./SupportAvatar";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useSupportSocket } from "../../hooks/useSupportSocket";
import { useGetAllTeachers } from "../../query/teacherQuery";
import { useGetAllParents } from "../../query/parentQuery";
import { useGetAllChildren } from "../../query/childQuery";
import {
  useCreateSupportTicket,
  useGetAllSupportTickets,
  useGetSupportMessages,
  useGetSupportTicketById,
  useMarkSupportTicketRead,
  useSendSupportMessage,
  useUpdateSupportTicket,
} from "../../query/supportQuery";
import {
  QUICK_REPLIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  childLabel,
  hasAdminUnread,
  priorityTone,
  statusTone,
  ticketCategoryLabel,
  ticketClock,
  ticketDayLabel,
} from "./supportHelpers";
import "../teachers/teachers.css";
import "./support.css";

function SupportSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  label = "",
  className = "",
  align = "left",
  variant = "default",
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const selected = options.find((item) => String(item.value) === String(value ?? ""));
  const isStatus = variant === "status";
  const isPriority = variant === "priority";
  const isPill = isStatus || isPriority;
  const toneFor = isPriority ? priorityTone : statusTone;

  const updatePosition = () => {
    const box = triggerRef.current?.getBoundingClientRect();
    if (!box) return;
    const width = Math.max(box.width, isPill ? 188 : box.width);
    const left = align === "right" ? box.right - width : box.left;
    setPos({ top: box.bottom + 8, left, width });
  };

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();

    const onDoc = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onLayout = () => updatePosition();

    window.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align, isPill]);

  const triggerClass = isPill
    ? `support-select-trigger is-status${selected?.value ? ` ${toneFor(selected.value)}` : ""}${open ? " is-expanded" : ""}${className ? ` ${className}` : ""}`
    : `bw-select-trigger${open ? " is-open" : ""}${className ? ` ${className}` : ""}`;

  const menuClass = isPill ? "support-select-menu" : "bw-select-menu";

  const control = (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label || placeholder}
      >
        {isPill ? (
          <span>{selected?.label || placeholder}</span>
        ) : (
          <span className={selected ? "" : "is-placeholder"}>{selected?.label || placeholder}</span>
        )}
        <ChevronDown size={14} />
      </button>
      {open
        ? createPortal(
            <div ref={menuRef} className={menuClass} style={{ top: pos.top, left: pos.left, width: pos.width }}>
              {options.map((item) => {
                const active = String(item.value) === String(value ?? "");
                return (
                  <button
                    key={String(item.value)}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`support-select-option${active ? " is-active" : ""}`}
                    onClick={() => {
                      onChange(item.value);
                      setOpen(false);
                    }}
                  >
                    <span className="support-select-option-main">
                      {isPill && item.value ? (
                        <span className={`support-menu-pill ${toneFor(item.value)}`}>{item.label}</span>
                      ) : (
                        <span>{item.label}</span>
                      )}
                    </span>
                    {active ? <Check size={16} /> : null}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </>
  );

  if (!label) return control;

  return (
    <label className="support-head-select">
      <span>{label}</span>
      {control}
    </label>
  );
}

function Support() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: activeTicketId } = useParams();
  const token = useSelector((state) => state.user.userToken);
  const user = useSelector((state) => state.user.userData);

  const [searchTerm, setSearchTerm] = useState("");
  const [listScope, setListScope] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPreset, setCreatePreset] = useState(null);
  const [draft, setDraft] = useState("");

  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const hasFilters = Boolean(statusFilter || priorityFilter);

  useEffect(() => {
    if (!filterOpen) return;
    setSelectedStatus(statusFilter);
    setSelectedPriority(priorityFilter);
  }, [filterOpen, statusFilter, priorityFilter]);

  const applyFilter = () => {
    setStatusFilter(selectedStatus || "");
    setPriorityFilter(selectedPriority || "");
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setSelectedStatus("");
    setSelectedPriority("");
    setStatusFilter("");
    setPriorityFilter("");
    setFilterOpen(false);
  };

  useEffect(() => {
    const preset = location.state?.openCreate ? location.state : null;
    if (!preset) return;

    setCreatePreset({
      participantRole: preset.participantRole,
      participant: preset.participant,
      relatedChild: preset.relatedChild,
      subject: preset.subject,
    });
    setCreateOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useSupportSocket(token, { activeTicketId });

  const queryParams = useMemo(
    () => ({
      page: "1",
      limit: "50",
      keyword: debouncedSearch,
      ...(statusFilter && { status: statusFilter }),
      ...(priorityFilter && { priority: priorityFilter }),
    }),
    [debouncedSearch, statusFilter, priorityFilter]
  );

  const { data: ticketsData, isLoading, isFetching } = useGetAllSupportTickets(queryParams);
  const { data: activeTicket } = useGetSupportTicketById(activeTicketId);
  const { data: messagesData, isLoading: loadingMessages } = useGetSupportMessages(activeTicketId, {
    page: "1",
    limit: "200",
  });

  const { data: teachersData, isLoading: loadingTeachers } = useGetAllTeachers({
    page: "1",
    limit: "200",
    status: "ACTIVE",
  });
  const { data: parentsData, isLoading: loadingParents } = useGetAllParents({
    page: "1",
    limit: "200",
    status: "ACTIVE",
  });
  const { data: childrenData } = useGetAllChildren({ page: "1", limit: "200", status: "ACTIVE" });

  const { mutate: createTicket, isPending: creating } = useCreateSupportTicket();
  const { mutate: sendMessage, isPending: sending } = useSendSupportMessage();
  const { mutate: markRead } = useMarkSupportTicketRead();
  const { mutate: updateTicket } = useUpdateSupportTicket();

  const tickets = useMemo(() => ticketsData?.docs || [], [ticketsData]);
  const visibleTickets = useMemo(() => {
    const mineId = String(user?._id || "");
    let list = tickets;
    if (listScope === "unread") {
      list = list.filter((ticket) => Number(ticket.adminUnreadCount || 0) > 0);
    }
    if (listScope === "mine") {
      list = list.filter((ticket) => String(ticket.assignedAdmin?._id || ticket.assignedAdmin || "") === mineId);
    }
    return list;
  }, [tickets, listScope, user?._id]);
  const messages = useMemo(() => messagesData?.docs || [], [messagesData]);
  const messageRows = useMemo(() => {
    const rows = [];
    let lastDay = "";
    messages.forEach((message) => {
      const day = String(message.createdAt || "").slice(0, 10);
      if (day && day !== lastDay) {
        rows.push({ type: "day", id: `day-${day}`, label: ticketDayLabel(message.createdAt) });
        lastDay = day;
      }
      rows.push({ type: "message", id: message._id, message });
    });
    return rows;
  }, [messages]);
  const teachers = teachersData?.docs || [];
  const parents = parentsData?.docs || [];
  const children = childrenData?.docs || [];

  useEffect(() => {
    if (!activeTicketId || !activeTicket) return;
    if (hasAdminUnread(activeTicket)) {
      markRead(activeTicketId);
    }
  }, [activeTicketId, activeTicket, markRead]);

  useEffect(() => {
    if (!activeTicketId) return;
    const list = document.querySelector(".support-message-list");
    if (list) list.scrollTop = list.scrollHeight;
  }, [activeTicketId, messages.length, sending]);

  const handleCreateTicket = (form) => {
    createTicket(
      {
        subject: form.subject.trim(),
        message: form.message.trim(),
        participantRole: form.participantRole,
        participant: form.participant,
        category: form.category,
        priority: form.priority,
        ...(form.relatedChild && { relatedChild: form.relatedChild }),
      },
      {
        onSuccess: (response) => {
          setCreateOpen(false);
          setCreatePreset(null);
          const ticketId = response?.data?.ticket?._id;
          if (ticketId) navigate(`/support/${ticketId}`);
          swal("Success", "Message sent", "success");
        },
        onError: (error) => swal("Error", error?.message || "Failed to create ticket", "error"),
      }
    );
  };

  const handleSendMessage = () => {
    const body = draft.trim();
    if (!body || !activeTicketId || activeTicket?.status === "CLOSED") return;

    sendMessage(
      { ticketId: activeTicketId, body },
      {
        onSuccess: () => {
          setDraft("");
        },
        onError: (error) => swal("Error", error?.message || "Failed to send message", "error"),
      }
    );
  };

  const handleStatusChange = (status) => {
    if (!activeTicketId || !status) return;
    updateTicket(
      { id: activeTicketId, status },
      {
        onError: (error) => swal("Error", error?.message || "Failed to update ticket", "error"),
      }
    );
  };

  const handlePriorityChange = (priority) => {
    if (!activeTicketId || !priority) return;
    updateTicket(
      { id: activeTicketId, priority },
      {
        onError: (error) => swal("Error", error?.message || "Failed to update priority", "error"),
      }
    );
  };

  return (
    <div className="data-page support-page">
      <div className="support-layout">
        <aside className="support-sidebar">
          <div className="support-sidebar-head">
            <h2>
              Messages
              <span>{visibleTickets.length}</span>
            </h2>
            <button type="button" className="support-sidebar-new" onClick={() => setCreateOpen(true)} aria-label="New message">
              <Plus size={16} />
            </button>
          </div>

          <div className="support-sidebar-tools">
            <label className="support-sidebar-search">
              <Search size={15} />
              <input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
            </label>
            <button
              type="button"
              className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
              aria-label="Filter"
              onClick={() => setFilterOpen(true)}
            >
              <Filter size={16} />
              {hasFilters ? <span className="data-filter-dot" /> : null}
            </button>
          </div>

          <div className="support-scope-row">
            {[
              { value: "all", label: "All" },
              { value: "unread", label: "Unread" },
              { value: "mine", label: "Mine" },
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                className={`support-scope-chip${listScope === item.value ? " is-active" : ""}`}
                onClick={() => setListScope(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="support-ticket-list">
            {isLoading ? (
              <div className="support-skel-list" aria-busy="true" aria-label="Loading messages">
                {Array.from({ length: 6 }, (_, index) => (
                  <div className="support-skel-ticket" key={index}>
                    <span className="support-skel-avatar" />
                    <span className="support-skel-copy">
                      <span className="support-skel-line is-title" />
                      <span className="support-skel-line" />
                    </span>
                  </div>
                ))}
              </div>
            ) : visibleTickets.length === 0 ? (
              <div className="support-empty is-compact">
                <MessageSquare size={28} strokeWidth={1.5} />
                <h3>No messages</h3>
                <p>Start a conversation with a teacher or parent.</p>
                <button type="button" className="data-primary is-small" onClick={() => setCreateOpen(true)}>
                  <Plus size={16} />
                  New message
                </button>
              </div>
            ) : (
              visibleTickets.map((ticket) => {
                const active = activeTicketId === ticket._id;
                const unreadCount = Number(ticket.adminUnreadCount || 0);
                return (
                  <button
                    key={ticket._id}
                    type="button"
                    className={`support-ticket-item${active ? " is-active" : ""}${unreadCount > 0 ? " is-unread" : ""}`}
                    onClick={() => navigate(`/support/${ticket._id}`)}
                  >
                    <span className="support-ticket-copy">
                      <span className="support-ticket-row">
                        <span className="support-ticket-number">{ticket.ticketNumber}</span>
                        <span className="support-ticket-when">
                          <time>{ticketClock(ticket.lastMessageAt || ticket.updatedAt)}</time>
                          {unreadCount > 0 ? (
                            <span className="support-unread-count">{unreadCount > 99 ? "99+" : unreadCount}</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="support-ticket-name">{ticket.participantName}</span>
                      <span className="support-ticket-subject">{ticket.subject || "No subject"}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="support-chat-panel">
          {!activeTicketId || !activeTicket ? (
            <div className="support-empty">
              <span className="support-empty-icon">
                <MessageSquare size={36} strokeWidth={1.5} />
              </span>
              <h3>Select a conversation</h3>
              <p>Pick someone from the list or send a new message.</p>
              <button type="button" className="data-primary" onClick={() => setCreateOpen(true)}>
                <Plus size={18} />
                New message
              </button>
            </div>
          ) : (
            <>
              <div className="support-chat-head">
                <div className="support-chat-head-main">
                  <SupportAvatar
                    ticket={activeTicket}
                    teachers={teachers}
                    parents={parents}
                    className="support-chat-avatar"
                    size={40}
                  />
                  <div className="support-chat-head-copy">
                    <h2>{activeTicket.subject || "No subject"}</h2>
                    <p className="support-chat-person">
                      <span className="support-chat-number">{activeTicket.ticketNumber}</span>
                      {activeTicket.participantName}
                    </p>
                    <p>
                      {ticketCategoryLabel(activeTicket.category)}
                      {activeTicket.relatedChild ? ` · ${childLabel(activeTicket.relatedChild)}` : ""}
                    </p>
                  </div>
                </div>
                <div className="support-chat-head-status">
                  <SupportSelect
                    label="Status"
                    className="support-ticket-status"
                    value={activeTicket.status}
                    onChange={handleStatusChange}
                    placeholder="Status"
                    variant="status"
                    align="right"
                    options={TICKET_STATUSES.filter((item) => item.value)}
                  />
                  <SupportSelect
                    label="Priority"
                    className="support-ticket-status"
                    value={activeTicket.priority}
                    onChange={handlePriorityChange}
                    placeholder="Priority"
                    variant="priority"
                    align="right"
                    options={TICKET_PRIORITIES}
                  />
                </div>
              </div>

              <div className="support-chat-body">
                <div className="support-message-list">
                  {loadingMessages ? (
                    <div className="support-skel-thread" aria-busy="true" aria-label="Loading conversation">
                      {Array.from({ length: 4 }, (_, index) => (
                        <div
                          className={`support-skel-bubble${index % 2 ? " is-mine" : ""}`}
                          key={index}
                        >
                          <span className="support-skel-line" />
                          <span className="support-skel-line is-short" />
                        </div>
                      ))}
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="support-empty is-compact">No messages yet. Send the first reply below.</div>
                  ) : (
                    messageRows.map((row) => {
                      if (row.type === "day") {
                        return (
                          <div key={row.id} className="support-day">
                            <span>{row.label}</span>
                          </div>
                        );
                      }
                      const message = row.message;
                      if (message.kind === "EVENT") {
                        return (
                          <div key={message._id} className="support-event">
                            <span>{message.body}</span>
                            <time>{ticketClock(message.createdAt)}</time>
                          </div>
                        );
                      }
                      const isAdmin = message.senderRole === "ADMIN";
                      const senderId = String(message.sender?._id || message.sender || "");
                      const adminImage =
                        message.senderImage ||
                        (senderId && senderId === String(user?._id || "") ? user?.image || "" : "");
                      const adminLabel =
                        message.senderName ||
                        `${user?.firstName || "Admin"} ${user?.lastName || ""}`.trim();
                      return (
                        <div
                          key={message._id}
                          className={`support-message${isAdmin ? " is-admin" : " is-user"}`}
                        >
                          {!isAdmin ? (
                            <SupportAvatar
                              ticket={activeTicket}
                              teachers={teachers}
                              parents={parents}
                              name={message.senderName}
                              image={message.senderImage}
                              className="support-bubble-avatar"
                              size={32}
                            />
                          ) : null}
                          <div className="support-message-stack">
                            <div className="support-message-bubble">
                              <div className="support-message-body">{message.body}</div>
                              <div className="support-message-meta">
                                <span>{ticketClock(message.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                          {isAdmin ? (
                            <SupportAvatar
                              name={adminLabel}
                              image={adminImage}
                              className="support-bubble-avatar is-agent"
                              size={32}
                            />
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="support-composer">
                  {activeTicket.status === "CLOSED" ? (
                    <p className="support-closed-note">This ticket is closed. Reopen it to reply.</p>
                  ) : (
                    <div className="support-quick-replies">
                      {QUICK_REPLIES.map((reply) => (
                        <button
                          key={reply.label}
                          type="button"
                          className="support-quick-reply"
                          onClick={() => setDraft(reply.body)}
                        >
                          {reply.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="support-composer-row">
                    <textarea
                      placeholder={
                        activeTicket.status === "CLOSED" ? "Ticket is closed" : "Type a reply..."
                      }
                      value={draft}
                      disabled={activeTicket.status === "CLOSED"}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="support-send-btn"
                      disabled={!draft.trim() || sending || activeTicket.status === "CLOSED"}
                      onClick={handleSendMessage}
                      aria-label="Send message"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <CreateTicketModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreatePreset(null);
        }}
        onSubmit={handleCreateTicket}
        creating={creating}
        initialValues={createPreset ?? undefined}
        teachers={teachers}
        parents={parents}
        children={children}
        loadingLists={loadingTeachers || loadingParents}
      />

      <Drawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Status</p>
          <SupportSelect
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="All statuses"
            variant="status"
            options={TICKET_STATUSES}
          />
        </div>
        <div className="bw-filter-field">
          <p className="bw-filter-label">Priority</p>
          <SupportSelect
            value={selectedPriority}
            onChange={setSelectedPriority}
            placeholder="All priorities"
            variant="priority"
            options={[{ value: "", label: "All priorities" }, ...TICKET_PRIORITIES]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default Support;
